import { getAudioEngine } from '@/infra/audio/AudioEngine';
import { eventBus } from '@/infra/event-bus/EventBus';
import { useStore } from '@/infra/store';
import { logger } from '@/infra/logger/Logger';
import { getSfxDef } from '@/domain/audio/sfx-catalog';
import { getLoopDef } from '@/domain/audio/loop-catalog';
import type { SfxId, AmbientId, MusicId } from '@/domain/audio/types';
import type { BiomeId } from '@/domain/world/types';
import { dayPhaseFromMs } from '@/domain/world/dayphase';

const FADE_AMBIENT_MS = 1500;
const FADE_MUSIC_MS = 800;

let unsubs: Array<() => void> = [];
let started = false;

function masterMultFor(category: 'sfx' | 'ambient' | 'music'): number {
  const s = useStore.getState().settings.audio;
  const cat = category === 'sfx' ? s.sfxVolume : category === 'ambient' ? s.ambientVolume : s.musicVolume;
  return cat;
}

function applySettings(): void {
  const eng = getAudioEngine();
  const s = useStore.getState().settings.audio;
  eng.setMaster(s.masterVolume);
  eng.setMuted(s.muted);
  eng.setCategoryGain('sfx', s.sfxVolume);
  eng.setCategoryGain('ambient', s.ambientVolume);
  eng.setCategoryGain('music', s.musicVolume);
}

function playSfx(id: SfxId): void {
  const eng = getAudioEngine();
  if (!eng.isUnlocked()) return;
  const def = getSfxDef(id);
  if (!def) return;
  eng.playSfx(def, masterMultFor('sfx'));
}

function setAmbient(id: AmbientId | null): void {
  const eng = getAudioEngine();
  if (!eng.isUnlocked()) return;
  if (id === null) {
    eng.stopLoop('ambient', FADE_AMBIENT_MS);
    return;
  }
  const def = getLoopDef(id);
  if (!def) return;
  eng.startLoop('ambient', def, FADE_AMBIENT_MS, masterMultFor('ambient'));
}

function setMusic(id: MusicId | null): void {
  const eng = getAudioEngine();
  if (!eng.isUnlocked()) return;
  if (id === null) {
    eng.stopLoop('music', FADE_MUSIC_MS);
    return;
  }
  const def = getLoopDef(id);
  if (!def) return;
  eng.startLoop('music', def, FADE_MUSIC_MS, masterMultFor('music'));
}

function ambientForCurrentLocation(): AmbientId {
  const s = useStore.getState();
  if (s.inWorld || s.inExpedition) {
    return s.world.lastBiome ?? 'meadow';
  }
  // House
  return dayPhaseFromMs(Date.now()) === 'day' ? 'house-day' : 'house-night';
}

async function unlock(): Promise<void> {
  const eng = getAudioEngine();
  await eng.unlock();
  applySettings();
  // Toca ambient inicial assim que destrava
  setAmbient(ambientForCurrentLocation());
}

function start(): void {
  if (started) return;
  started = true;
  // SFX listeners
  unsubs.push(eventBus.on('creature.fed', () => playSfx('feed')));
  unsubs.push(eventBus.on('creature.played', () => playSfx('play')));
  unsubs.push(eventBus.on('creature.cleaned', () => playSfx('clean')));
  unsubs.push(eventBus.on('creature.disciplined', () => playSfx('discipline')));
  unsubs.push(eventBus.on('creature.healed', () => playSfx('heal')));
  unsubs.push(eventBus.on('creature.egg-wiggle', () => playSfx('egg-wiggle')));
  unsubs.push(eventBus.on('creature.hatched', () => playSfx('hatch')));
  unsubs.push(eventBus.on('world.item-collected', () => playSfx('pickup')));
  unsubs.push(eventBus.on('breeding.completed', () => playSfx('breeding')));
  unsubs.push(eventBus.on('npc.died', () => playSfx('death')));
  unsubs.push(eventBus.on('combat.player-hit', () => playSfx('hit')));
  unsubs.push(
    eventBus.on('combat.enemy-hit', (p) => {
      if (p.killed) playSfx('enemy-die');
      else playSfx('attack');
    }),
  );
  unsubs.push(eventBus.on('combat.skill-used', () => playSfx('attack')));
  unsubs.push(eventBus.on('combat.mutation-applied', () => playSfx('mutation')));

  // Ambient
  unsubs.push(eventBus.on('world.entered', (p) => setAmbient(p.biome as BiomeId)));
  unsubs.push(eventBus.on('world.biome-changed', (p) => setAmbient(p.to as BiomeId)));
  unsubs.push(
    eventBus.on('world.exited', () => setAmbient(ambientForCurrentLocation())),
  );

  // Music
  unsubs.push(eventBus.on('combat.started', () => setMusic('combat')));
  unsubs.push(eventBus.on('combat.ended', () => setMusic(null)));

  // Settings live updates
  let lastAudioJson = JSON.stringify(useStore.getState().settings.audio);
  unsubs.push(
    useStore.subscribe((state) => {
      const j = JSON.stringify(state.settings.audio);
      if (j !== lastAudioJson) {
        lastAudioJson = j;
        applySettings();
      }
    }),
  );

  // Boot listener para unlock no primeiro click
  const onceClick = (): void => {
    void unlock().then(() => {
      logger.info('audio.unlocked');
    });
    document.removeEventListener('pointerdown', onceClick);
    document.removeEventListener('keydown', onceClick);
  };
  document.addEventListener('pointerdown', onceClick, { once: true });
  document.addEventListener('keydown', onceClick, { once: true });
}

function stop(): void {
  unsubs.forEach((u) => u());
  unsubs = [];
  started = false;
  getAudioEngine().dispose();
}

export interface AudioServiceApi {
  start: () => void;
  stop: () => void;
  unlock: () => Promise<void>;
  playSfx: (id: SfxId) => void;
  setAmbient: (id: AmbientId | null) => void;
  setMusic: (id: MusicId | null) => void;
}

export function getAudioService(): AudioServiceApi {
  return { start, stop, unlock, playSfx, setAmbient, setMusic };
}

export function resetAudioServiceForTests(): void {
  stop();
}
